import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Store, Package, Truck, CheckCircle, Info, Check, X, Lock, ShieldCheck,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

import { SquarePaymentForm } from "../components/SquarePaymentForm";
import SendcloudServicePointPicker from "../components/SendcloudServicePointPicker";
import { extractApiError } from "../components/AuthPages";

import { useAuth, useCart } from "../contexts/AppContexts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { user, setUser, login, loading: authLoading } = useAuth();
  const { cart, total: cartTotal, clearCart } = useCart();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState("click_collect");
  const [shippingOptions, setShippingOptions] = useState([]);
  const [pickupSlots, setPickupSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [shippingCost, setShippingCost] = useState(0);
  const [shippingAddress, setShippingAddress] = useState({
    address_line_1: "",
    city: "",
    postal_code: "",
    country: "France"
  });
  const [mrPoint, setMrPoint] = useState(null);  // selected Mondial Relay parcel-shop (POINT mode)
  const [mrBrand, setMrBrand] = useState("");

  // Load Mondial Relay brand code (needed by the embedded widget)
  useEffect(() => {
    axios.get(`${API}/mondial-relay/config`)
      .then(r => setMrBrand(r.data?.brand || ""))
      .catch(() => {});
  }, []);
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: ""
  });
  const [loading, setLoading] = useState(false);
  const [paymentStep, setPaymentStep] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [orderComplete, setOrderComplete] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [squareConfig, setSquareConfig] = useState(null);
  // Code promo
  const [promoInput, setPromoInput] = useState("");
  const [promoApplied, setPromoApplied] = useState(null); // { code, discount_amount, discount_type, value }
  const [promoLoading, setPromoLoading] = useState(false);
  // Choix paiement pour Click & Collect
  const [clickCollectPayment, setClickCollectPayment] = useState("online"); // "online" | "on_pickup"
  const [rgpdConsent, setRgpdConsent] = useState(false);

  // Scroll automatique vers le haut a chaque changement d'etape (paiement / confirmation)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [paymentStep, orderComplete]);

  // Arrondi pour eviter les artefacts de virgule flottante (ex: 0.89 + 5 = 5.890000000000001)
  const discountAmount = promoApplied?.discount_amount || 0;
  const finalTotal = Math.round((cartTotal + shippingCost - discountAmount) * 100) / 100;

  // Panier composé UNIQUEMENT de cartes cadeaux virtuelles → on masque
  // toutes les étapes physiques (livraison, retrait, adresse) et on route
  // directement vers le paiement Square. Bug remonté client 03/2026.
  const isGiftCardOnly = cart.length > 0 && cart.every(i => i.is_gift_card);

  // Force le mode "digital" pour les cartes cadeaux et repasse en click_collect
  // par défaut si le panier redevient physique.
  useEffect(() => {
    if (isGiftCardOnly && deliveryMethod !== "digital") {
      setDeliveryMethod("digital");
      setShippingCost(0);
    } else if (!isGiftCardOnly && deliveryMethod === "digital") {
      setDeliveryMethod("click_collect");
    }
  }, [isGiftCardOnly, deliveryMethod]);

  // Indicateur d'etape sticky pour le tunnel de checkout
  const checkoutStep = orderComplete ? 3 : paymentStep ? 2 : 1;
  const CheckoutProgress = () => (
    <div className="sticky top-20 z-30 -mx-6 px-6 py-3 mb-6 bg-[#1B132B]/85 backdrop-blur-md border-b border-white/5" data-testid="checkout-steps-sticky">
      <div className="flex items-center justify-center gap-2" data-testid="checkout-steps">
        {[{num:1,label:'Informations'},{num:2,label:'Paiement'},{num:3,label:'Confirmation'}].map((s, i) => (
          <React.Fragment key={s.num}>
            {i > 0 && <div className={`w-8 h-px ${checkoutStep >= s.num ? 'bg-[#D926A9]' : 'bg-white/10'}`} />}
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${checkoutStep === s.num ? 'bg-[#D926A9] text-white' : checkoutStep > s.num ? 'bg-[#D926A9]/20 text-[#D926A9]' : 'bg-white/5 text-[#B59ED1]'}`}
              data-testid={`checkout-step-${s.num}`}
            >
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] bg-white/10">{checkoutStep > s.num ? '✓' : s.num}</span>
              <span className="hidden sm:inline">{s.label}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
      <div className="mt-2 h-0.5 bg-white/5 rounded-full overflow-hidden max-w-md mx-auto">
        <motion.div
          className="h-full bg-gradient-to-r from-[#D926A9] to-[#00E5FF]"
          initial={false}
          animate={{ width: ((checkoutStep - 1) / 2) * 100 + '%' }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        />
      </div>
    </div>
  );

  useEffect(() => {
    // 1. Redirect if cart is empty (rien à payer)
    if (cart.length === 0 && !orderComplete) {
      navigate('/boutique');
      return;
    }

    // 2. AuthGate : le checkout est réservé aux utilisateurs connectés.
    // On affiche maintenant un modal IN-PAGE (au lieu de naviguer vers /connexion)
    // pour éviter à l'utilisateur de remonter en haut de page après login.
    if (authLoading) return;
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    setShowLoginModal(false);

    // Fetch data
    const fetchData = async () => {
      try {
        // Skip livraison + créneaux pour un panier 100% carte cadeau virtuelle
        if (!isGiftCardOnly) {
          const slotsRes = await axios.get(`${API}/click-collect/slots`);
          setPickupSlots(slotsRes.data.slots || []);
          const shippingRes = await axios.post(`${API}/shipping/calculate`, {
            items: cart.map(item => ({ product_id: item.base_product_id || item.product_id, quantity: item.quantity })),
            subtotal: cartTotal
          });
          setShippingOptions(shippingRes.data.options || []);
        }

        // Fetch Square config
        const configRes = await axios.get(`${API}/payments/config`);
        setSquareConfig(configRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();

    // Pre-fill customer info (user is guaranteed to be logged in here)
    setCustomerInfo({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || ""
    });

    // Pre-fill delivery preferences (adresse + dernier point relais utilisés)
    const prefs = user.delivery_preferences || {};
    if (prefs.preferred_method) {
      setDeliveryMethod(prefs.preferred_method);
    }
    if (prefs.address_line_1 || prefs.city || prefs.postal_code) {
      setShippingAddress({
        address_line_1: prefs.address_line_1 || "",
        address_line_2: prefs.address_line_2 || "",
        city: prefs.city || "",
        postal_code: prefs.postal_code || "",
        country: prefs.country || "France",
      });
    }
    if (prefs.mr_point && (prefs.mr_point.id || prefs.mr_point.ID)) {
      setMrPoint(prefs.mr_point);
    }
  }, [cart, cartTotal, navigate, orderComplete, user, authLoading]);

  // Update shipping cost when delivery method changes
  useEffect(() => {
    const selectedOption = shippingOptions.find(opt => opt.method === deliveryMethod);
    setShippingCost(selectedOption?.price || 0);
  }, [deliveryMethod, shippingOptions]);

  // Re-valider le code promo si le sous-total change (panier modifie ailleurs)
  useEffect(() => {
    if (!promoApplied || !user) return;
    let cancel = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/promo-codes/validate`, {
          code: promoApplied.code, subtotal: cartTotal
        }, { withCredentials: true });
        if (cancel) return;
        if (res.data?.valid) {
          setPromoApplied(res.data);
        } else {
          setPromoApplied(null);
          toast.info("Le code promo n'est plus applicable");
        }
      } catch (e) { /* silent */ }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartTotal]);

  const handleApplyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    if (!user) { toast.info("Connecte-toi pour utiliser un code promo"); login(); return; }
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API}/promo-codes/validate`, {
        code, subtotal: cartTotal
      }, { withCredentials: true });
      if (res.data?.valid) {
        setPromoApplied(res.data);
        toast.success(`Code "${res.data.code}" appliqué : -${res.data.discount_amount.toFixed(2)} €`);
      } else {
        setPromoApplied(null);
        toast.error(res.data?.error || "Code promo invalide");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de validation");
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoApplied(null);
    setPromoInput("");
  };

  // Group slots by date
  const slotsByDate = pickupSlots.reduce((acc, slot) => {
    if (!acc[slot.date]) {
      acc[slot.date] = {
        formatted: slot.formatted_date,
        day_name: slot.day_name,
        slots: []
      };
    }
    acc[slot.date].slots.push(slot);
    return acc;
  }, {});

  const handleCreateOrder = async () => {
    if (!user) {
      toast.info("Connecte-toi pour commander");
      login();
      return;
    }

    if (!rgpdConsent) {
      toast.error("Merci d'accepter notre politique de confidentialité");
      return;
    }

    // Validation
    if (deliveryMethod === "click_collect") {
      if (!selectedSlot) {
        toast.error("Sélectionne un créneau de retrait");
        return;
      }
    }

    // Adresse obligatoire pour TOUS les modes de livraison PHYSIQUE (fichier client, facture, fidélité).
    // Carte cadeau virtuelle (delivery_method === "digital") : on n'exige que nom + email.
    if (deliveryMethod !== "digital") {
      if (!shippingAddress.address_line_1 || !shippingAddress.city || !shippingAddress.postal_code) {
        toast.error("Merci de renseigner ton adresse postale (obligatoire)");
        return;
      }
      if (!customerInfo.name || !customerInfo.phone) {
        toast.error("Merci de renseigner ton nom et ton téléphone");
        return;
      }
    } else {
      if (!customerInfo.name || !customerInfo.email) {
        toast.error("Merci de renseigner ton nom et ton email");
        return;
      }
    }

    setLoading(true);
    try {
      // Sauvegarde silencieuse du téléphone dans le profil si l'utilisateur l'a modifié
      // → Plus besoin de le retaper aux prochaines commandes
      if (customerInfo.phone && customerInfo.phone !== user?.phone) {
        try {
          await axios.put(`${API}/auth/profile`, { phone: customerInfo.phone }, { withCredentials: true });
        } catch (_e) { /* non bloquant — la commande continue */ }
      }
      const orderData = {
        items: cart.map(item => ({
          product_id: item.base_product_id || item.product_id,
          quantity: item.quantity,
          variation_id: item.variation_id || null
        })),
        delivery_method: deliveryMethod,
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
        shipping_cost: shippingCost,
        promo_code: promoApplied?.code || null,
        payment_method: deliveryMethod === "click_collect" ? clickCollectPayment : "online"
      };

      if (deliveryMethod === "click_collect" && selectedSlot) {
        orderData.pickup_date = selectedSlot.date;
        orderData.pickup_time_slot = selectedSlot.time_slot;
      }

      if (deliveryMethod === "mondial_relay_home") {
        orderData.shipping_address = shippingAddress;
      }
      // Mondial Relay / Sendcloud POINT — require a service-point selection
      if (deliveryMethod === "mondial_relay_point") {
        const pointId = mrPoint?.id || mrPoint?.ID;
        if (!mrPoint || !pointId) {
          toast.error("Sélectionne un point relais sur la carte avant de continuer");
          return;
        }
        orderData.point_relais = mrPoint;
        // Normalisation : on accepte les 2 formats (Sendcloud → camelCase + id, MondialRelay → PascalCase)
        const pName = mrPoint.name || mrPoint.Nom || "";
        const pAddr = mrPoint.street || mrPoint.Adresse1 || "";
        const pAddr2 = mrPoint.house_number || mrPoint.Adresse2 || "";
        const pCP = mrPoint.postal_code || mrPoint.CP || shippingAddress.postal_code;
        const pCity = mrPoint.city || mrPoint.Ville || shippingAddress.city;
        orderData.shipping_address = {
          ...shippingAddress,
          address_line_1: `${pName} — ${pAddr} ${pAddr2}`.trim(),
          postal_code: pCP,
          city: pCity,
        };
      }

      const response = await axios.post(`${API}/orders`, orderData, { withCredentials: true });

      if (response.data.success) {
        setOrderId(response.data.order_id);

        // Sauvegarde silencieuse des préférences de livraison pour les prochains achats.
        // L'adresse est désormais OBLIGATOIRE pour tous les modes → on la sauve systématiquement.
        const prefs = {
          preferred_method: deliveryMethod,
          address_line_1: shippingAddress.address_line_1,
          address_line_2: shippingAddress.address_line_2 || "",
          postal_code: shippingAddress.postal_code,
          city: shippingAddress.city,
          country: shippingAddress.country || "France",
        };
        if (deliveryMethod === "mondial_relay_point" && mrPoint?.ID) {
          prefs.mr_point = mrPoint;
        }
        try {
          axios.put(`${API}/auth/delivery-preferences`, prefs, { withCredentials: true });
        } catch (_e) { /* best-effort */ }

        // Si Click & Collect ET paiement en boutique → commande validée directement
        if (deliveryMethod === "click_collect" && clickCollectPayment === "on_pickup") {
          setCompletedOrder(response.data);
          setOrderComplete(true);
          clearCart();
          toast.success("Commande confirmée ! Paiement à effectuer en boutique.");
        } else {
          // Sinon (Click & Collect en ligne OU Mondial Relay) → passer au paiement
          setPaymentStep(true);
          toast.success("Commande créée. Procédez au paiement.");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la commande");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (sourceId, verificationToken) => {
    if (!orderId) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/payments/create`, {
        source_id: sourceId,
        order_id: orderId,
        verification_token: verificationToken
      }, { withCredentials: true });

      if (response.data.success) {
        setCompletedOrder({
          order_id: orderId,
          total: finalTotal,
          delivery_method: deliveryMethod,
          shipping_address: shippingAddress,
          receipt_url: response.data.receipt_url
        });
        setOrderComplete(true);
        clearCart();
        toast.success("Paiement réussi !");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de paiement");
    } finally {
      setLoading(false);
    }
  };

  // Order confirmation screen
  if (orderComplete && completedOrder) {
    return (
      <main className="pt-24 min-h-screen bg-[#1B132B]">
        <div className="max-w-2xl mx-auto px-6 py-16">
          <CheckoutProgress />
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="font-serif text-4xl mb-4">Commande Confirmée !</h1>
            <p className="text-[#B59ED1] mb-8">
              Merci pour ta commande. Voici les détails.
            </p>

            <Card className="bg-[#261B3B] border-white/10 text-left mb-8">
              <CardContent className="p-6 space-y-4">
                <div className="flex justify-between">
                  <span className="text-[#B59ED1]">N° de commande</span>
                  <span className="font-mono">{completedOrder.order_id}</span>
                </div>
                <Separator className="bg-white/10" />
                <div className="flex justify-between">
                  <span className="text-[#B59ED1]">Total</span>
                  <span className="font-serif text-xl text-[#D926A9]">{completedOrder.total?.toFixed(2)} €</span>
                </div>

                {completedOrder.delivery_method === "click_collect" && (
                  <>
                    <Separator className="bg-white/10" />
                    <div className="bg-[#1B132B] rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <Store className="w-5 h-5 text-[#00E5FF]" />
                        <span className="font-medium">Retrait en boutique</span>
                      </div>
                      <p className="text-[#B59ED1] text-sm mb-2">
                        <strong>Date :</strong> {completedOrder.pickup_date}
                      </p>
                      <p className="text-[#B59ED1] text-sm mb-2">
                        <strong>Heure :</strong> {completedOrder.pickup_time_slot}
                      </p>
                      <p className="text-[#B59ED1] text-sm">
                        <strong>Adresse :</strong> 20 quai du Roubion, 26200 Montélimar
                      </p>
                    </div>
                    <div className="bg-[#D926A9]/10 rounded-lg p-4 flex items-start gap-3">
                      <Info className="w-5 h-5 text-[#D926A9] mt-0.5" />
                      <p className="text-sm text-[#E2D5F8]">
                        Paiement à effectuer en boutique lors du retrait.
                      </p>
                    </div>
                  </>
                )}

                {completedOrder.delivery_method?.includes("mondial_relay") && (
                  <>
                    <Separator className="bg-white/10" />
                    <div className="bg-[#1B132B] rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <Truck className="w-5 h-5 text-[#00E5FF]" />
                        <span className="font-medium">Livraison Mondial Relay</span>
                      </div>
                      {completedOrder.shipping_address && (
                        <p className="text-[#B59ED1] text-sm">
                          {completedOrder.shipping_address.address_line_1}<br />
                          {completedOrder.shipping_address.postal_code} {completedOrder.shipping_address.city}
                        </p>
                      )}
                    </div>
                    {completedOrder.receipt_url && (
                      <a href={completedOrder.receipt_url} target="_blank" rel="noopener noreferrer" className="block text-center text-[#00E5FF] text-sm hover:underline">
                        Voir le reçu de paiement
                      </a>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate('/dashboard')} variant="outline" className="border-[#00E5FF]/40 text-[#00E5FF]">
                Voir mes commandes
              </Button>
              <Button onClick={() => navigate('/boutique')} className="gradient-primary">
                Continuer mes achats
              </Button>
            </div>
          </motion.div>
        </div>
      </main>
    );
  }

  // Payment step (Square Web Payments SDK)
  if (paymentStep && orderId) {
    return (
      <main className="pt-24 min-h-screen bg-[#1B132B]">
        <div className="max-w-xl mx-auto px-6 py-16">
          <CheckoutProgress />
          <div className="text-center mb-8">
            <h1 className="font-serif text-4xl mb-4">Paiement Sécurisé</h1>
            <p className="text-[#B59ED1]">Commande #{orderId.slice(-8)}</p>
          </div>

          <Card className="bg-[#261B3B] border-white/10 mb-4">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-lg">Total à payer</span>
                <span className="text-3xl font-serif text-[#D926A9]">{finalTotal.toFixed(2)} €</span>
              </div>
              <p className="text-xs text-[#B59ED1]">{isGiftCardOnly ? "Carte cadeau envoyée par email après validation" : "Livraison Mondial Relay incluse"}</p>
            </CardContent>
          </Card>

          <Card className="bg-[#261B3B] border-white/10">
            <CardContent className="p-6">
              {/* Bandeau de réassurance — placé EN DEHORS de SquarePaymentForm pour ne pas
                  perturber le timing de montage de l'iframe Square */}
              <div className="mb-4 rounded-xl border border-[#00E5FF]/25 bg-gradient-to-br from-[#00E5FF]/10 via-[#1B132B]/40 to-transparent p-3" data-testid="payment-security-banner">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-[#00E5FF]" />
                  <span className="text-sm font-medium text-[#F7F3FC]">Paiement 100% sécurisé</span>
                  <ShieldCheck className="w-4 h-4 text-emerald-400 ml-auto" />
                </div>
                <p className="text-[11px] text-[#B59ED1] leading-relaxed">
                  Tes coordonnées bancaires sont chiffrées (TLS 1.3) et transmises directement à <strong className="text-white">Square</strong>, certifié <strong className="text-white">PCI DSS</strong>. Elles ne sont jamais stockées sur nos serveurs.
                </p>
                <div className="flex items-center gap-2 mt-2.5">
                  <span title="Visa" className="inline-flex h-5 px-2 items-center rounded bg-white text-[10px] font-bold text-[#1A1F71]">VISA</span>
                  <span title="Mastercard" className="inline-flex h-5 px-2 items-center rounded bg-white">
                    <svg viewBox="0 0 32 20" className="h-3 w-auto"><circle cx="12" cy="10" r="6" fill="#EB001B" /><circle cx="20" cy="10" r="6" fill="#F79E1B" fillOpacity="0.85" /></svg>
                  </span>
                  <span title="American Express" className="inline-flex h-5 px-2 items-center rounded bg-[#006FCF] text-[9px] font-bold text-white tracking-wider">AMEX</span>
                  <span className="text-[10px] text-[#B59ED1] ml-1">3D Secure activé</span>
                </div>
              </div>

              <SquarePaymentForm
                amount={finalTotal}
                buyerEmail={customerInfo.email || user?.email || ""}
                buyerName={customerInfo.name || user?.name || ""}
                onSuccess={async (sourceId, verificationToken) => {
                  await handlePayment(sourceId, verificationToken);
                }}
                onError={() => toast.error("Erreur de paiement. Réessaie.")}
                buttonText="Payer"
              />
              <p className="text-[10px] text-[#B59ED1] mt-4 text-center">
                Paiement sécurisé par Square. Tes données bancaires ne sont jamais stockées sur nos serveurs.
              </p>
            </CardContent>
          </Card>

          {!isGiftCardOnly && (
          <button
            onClick={() => {
              setPaymentStep(false);
              setDeliveryMethod("click_collect");
            }}
            className="w-full mt-6 py-3 px-4 text-center text-base font-medium text-[#E2D5F8] hover:text-white bg-white/5 hover:bg-white/10 border border-white/15 rounded-lg transition-colors flex items-center justify-center gap-2"
            data-testid="back-to-click-collect"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18"/></svg>
            Revenir au Click &amp; Collect (paiement en boutique)
          </button>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24 min-h-screen bg-[#1B132B]">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <CheckoutProgress />
        <div className="text-center mb-12">
          <span className="text-[#00E5FF] text-sm uppercase tracking-[0.2em] mb-4 block">Finaliser ma commande</span>
          <h1 className="font-serif text-4xl md:text-5xl">Checkout</h1>
        </div>

        <div className={deliveryMethod === "mondial_relay_point" ? "space-y-6" : "grid md:grid-cols-2 gap-8"}>
          {/* Left: Delivery Options */}
          <div className="space-y-6">
            {/* Bandeau carte cadeau virtuelle — remplace tout le bloc livraison/retrait/adresse */}
            {isGiftCardOnly && (
              <Card className="bg-gradient-to-br from-[#D926A9]/15 via-[#261B3B] to-[#00E5FF]/10 border-[#D926A9]/30" data-testid="gift-card-only-banner">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#D926A9]/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-[#D926A9]" />
                    </div>
                    <div className="text-sm">
                      <p className="font-medium text-[#F7F3FC] mb-1">Carte cadeau 100% digitale</p>
                      <p className="text-[#B59ED1] leading-relaxed">
                        Ta carte cadeau te sera envoyée par email dès validation du paiement — aucun retrait ni livraison à prévoir. Il te suffit de compléter tes coordonnées et de payer.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Delivery Method — masqué pour les cartes cadeaux virtuelles */}
            {!isGiftCardOnly && (
            <Card className="bg-[#261B3B] border-white/10">
              <CardHeader>
                <CardTitle className="font-serif text-xl">Mode de livraison</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup value={deliveryMethod} onValueChange={setDeliveryMethod} className="space-y-4">
                  {/* Click & Collect */}
                  <div className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-all ${deliveryMethod === 'click_collect' ? 'border-[#D926A9] bg-[#D926A9]/10' : 'border-white/10 hover:border-white/30'}`}>
                    <RadioGroupItem value="click_collect" id="click_collect" className="mt-1" />
                    <Label htmlFor="click_collect" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2 mb-1">
                        <Store className="w-5 h-5 text-[#D926A9]" />
                        <span className="font-medium">Click & Collect</span>
                        <Badge className="bg-green-500/20 text-green-400 text-xs">GRATUIT</Badge>
                      </div>
                      <p className="text-[#B59ED1] text-sm">
                        20 quai du Roubion, 26200 Montélimar
                      </p>
                      <p className="text-[#00E5FF] text-xs mt-1" data-testid="shop-hours-checkout">
                        Horaires : consultez le pied de page
                      </p>
                    </Label>
                  </div>

                  {/* Mondial Relay Point Relais */}
                  {shippingOptions.filter(o => o.method === 'mondial_relay_point').map(option => (
                    <div key={option.method} className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-all ${deliveryMethod === option.method ? 'border-[#D926A9] bg-[#D926A9]/10' : 'border-white/10 hover:border-white/30'}`}>
                      <RadioGroupItem value={option.method} id={option.method} className="mt-1" />
                      <Label htmlFor={option.method} className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-2 mb-1">
                          <Package className="w-5 h-5 text-[#00E5FF]" />
                          <span className="font-medium">{option.name}</span>
                          {option.free ? (
                            <Badge className="bg-green-500/20 text-green-400 text-xs">OFFERT</Badge>
                          ) : (
                            <Badge className="bg-[#3A115E] text-[#E2D5F8] text-xs">{option.price.toFixed(2)} €</Badge>
                          )}
                        </div>
                        <p className="text-[#B59ED1] text-sm">{option.description}</p>
                        <p className="text-[#B59ED1] text-xs mt-1">{option.delay}</p>
                      </Label>
                    </div>
                  ))}

                  {/* Mondial Relay Domicile */}
                  {shippingOptions.filter(o => o.method === 'mondial_relay_home').map(option => (
                    <div key={option.method} className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-all ${deliveryMethod === option.method ? 'border-[#D926A9] bg-[#D926A9]/10' : 'border-white/10 hover:border-white/30'}`}>
                      <RadioGroupItem value={option.method} id={option.method} className="mt-1" />
                      <Label htmlFor={option.method} className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-2 mb-1">
                          <Truck className="w-5 h-5 text-[#00E5FF]" />
                          <span className="font-medium">{option.name}</span>
                          <Badge className="bg-[#3A115E] text-[#E2D5F8] text-xs">{option.price.toFixed(2)} €</Badge>
                        </div>
                        <p className="text-[#B59ED1] text-sm">{option.description}</p>
                        <p className="text-[#B59ED1] text-xs mt-1">{option.delay}</p>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>

                {cartTotal < 80 && (
                  <p className="text-sm text-[#00E5FF] mt-4 text-center">
                    Plus que {(80 - cartTotal).toFixed(2)} € pour la livraison gratuite en Point Relais !
                  </p>
                )}
              </CardContent>
            </Card>
            )}

            {/* Click & Collect Slots */}
            {!isGiftCardOnly && deliveryMethod === "click_collect" && (
              <Card className="bg-[#261B3B] border-white/10">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Créneau de retrait</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[250px] pr-4">
                    {Object.entries(slotsByDate).map(([date, data]) => (
                      <div key={date} className="mb-6">
                        <h4 className="font-medium text-[#00E5FF] mb-3">{data.formatted}</h4>
                        <div className="grid grid-cols-4 gap-2">
                          {data.slots.map((slot) => (
                            <button
                              key={`${slot.date}-${slot.time_slot}`}
                              onClick={() => setSelectedSlot(slot)}
                              className={`time-slot text-sm ${
                                selectedSlot?.date === slot.date && selectedSlot?.time_slot === slot.time_slot
                                  ? 'selected'
                                  : ''
                              }`}
                            >
                              {slot.time_slot}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </ScrollArea>

                  {selectedSlot && (
                    <div className="mt-4 p-3 bg-[#D926A9]/10 rounded-lg">
                      <p className="text-sm">
                        <span className="text-[#B59ED1]">Retrait prévu :</span>{" "}
                        <span className="font-medium">{selectedSlot.formatted_date} à {selectedSlot.time_slot}</span>
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Mondial Relay POINT — official parcel-shop picker widget
                Quand cette option est sélectionnée, la grille parente bascule
                en 1 colonne pleine largeur pour donner au widget l'espace
                nécessaire à son layout liste + carte. */}
            {!isGiftCardOnly && deliveryMethod === "mondial_relay_point" && (
              <Card className="bg-[#261B3B] border-white/10">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Choisis ton point relais</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 mb-4 max-w-md">
                    <div>
                      <Label className="text-[#B59ED1] text-sm">Code postal *</Label>
                      <Input
                        value={shippingAddress.postal_code}
                        onChange={(e) => setShippingAddress({ ...shippingAddress, postal_code: e.target.value })}
                        placeholder="75001"
                        className="bg-[#1B132B] border-white/10 mt-1"
                        data-testid="mr-postal-input"
                      />
                    </div>
                    <div>
                      <Label className="text-[#B59ED1] text-sm">Ville *</Label>
                      <Input
                        value={shippingAddress.city}
                        onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                        placeholder="Paris"
                        className="bg-[#1B132B] border-white/10 mt-1"
                      />
                    </div>
                  </div>
                  <SendcloudServicePointPicker
                    country="FR"
                    postalCode={shippingAddress.postal_code}
                    selected={mrPoint}
                    onSelect={setMrPoint}
                  />
                </CardContent>
              </Card>
            )}

            {/* Adresse personnelle — désormais OBLIGATOIRE pour tous les modes de livraison
                (fichier client, facture, fidélité). Pour mondial_relay_home elle sert aussi
                comme adresse de livraison. Masquée pour les cartes cadeaux virtuelles. */}
            {!isGiftCardOnly && (
            <Card className="bg-[#261B3B] border-white/10">
              <CardHeader>
                <CardTitle className="font-serif text-xl">
                  {deliveryMethod === "mondial_relay_home" ? "Adresse de livraison" : "Ton adresse postale"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-[#B59ED1] text-sm">Adresse *</Label>
                  <Input
                    value={shippingAddress.address_line_1}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, address_line_1: e.target.value })}
                    placeholder="123 rue de la Paix"
                    className="bg-[#1B132B] border-white/10 mt-1"
                    data-testid="checkout-address"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#B59ED1] text-sm">Code postal *</Label>
                    <Input
                      value={shippingAddress.postal_code}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, postal_code: e.target.value })}
                      placeholder="75001"
                      className="bg-[#1B132B] border-white/10 mt-1"
                      data-testid="checkout-postal-code"
                    />
                  </div>
                  <div>
                    <Label className="text-[#B59ED1] text-sm">Ville *</Label>
                    <Input
                      value={shippingAddress.city}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                      placeholder="Paris"
                      className="bg-[#1B132B] border-white/10 mt-1"
                      data-testid="checkout-city"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Customer Info */}
            <Card className="bg-[#261B3B] border-white/10">
              <CardHeader>
                <CardTitle className="font-serif text-xl">Tes coordonnées</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-[#B59ED1] text-sm">Nom complet *</Label>
                  <Input
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                    placeholder="Ton nom"
                    className="bg-[#1B132B] border-white/10 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[#B59ED1] text-sm">Email {isGiftCardOnly && '*'}</Label>
                  <Input
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                    placeholder="ton@email.com"
                    className="bg-[#1B132B] border-white/10 mt-1"
                  />
                  {isGiftCardOnly && (
                    <p className="text-[10px] text-[#B59ED1] mt-1">La carte cadeau sera envoyée à cette adresse.</p>
                  )}
                </div>
                {!isGiftCardOnly && (
                <div>
                  <Label className="text-[#B59ED1] text-sm">Téléphone *</Label>
                  <Input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                    placeholder="06 12 34 56 78"
                    className="bg-[#1B132B] border-white/10 mt-1"
                  />
                </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right: Order Summary */}
          <div>
            <Card className="bg-[#261B3B] border-white/10 sticky top-24">
              <CardHeader>
                <CardTitle className="font-serif text-xl">Récapitulatif</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="max-h-[300px] mb-4">
                  {cart.map((item) => (
                    <div key={item.product_id} className="flex items-center gap-4 py-3 border-b border-white/10" data-testid={`checkout-item-${item.product_id}`}>
                      <img
                        src={item.image_url || '/api/static/images/fallback_product.jpg'}
                        alt={item.name}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm line-clamp-1">{item.name}</p>
                        {item.variation_name && (
                          <p className="text-[11px] text-[#B59ED1] italic mt-0.5" data-testid={`checkout-item-variation-${item.product_id}`}>
                            Variante : {item.variation_name}
                          </p>
                        )}
                        <p className="text-[#B59ED1] text-sm mt-0.5">Qté: {item.quantity}</p>
                      </div>
                      <p className="font-serif text-[#D926A9]">{(item.price * item.quantity).toFixed(2)} €</p>
                    </div>
                  ))}
                </ScrollArea>

                <Separator className="bg-white/10 my-4" />

                {/* Code promo */}
                <div className="mb-4" data-testid="promo-code-section">
                  {!promoApplied ? (
                    <div className="space-y-2">
                      <label className="text-xs uppercase tracking-wider text-[#B59ED1]">Code promo</label>
                      <div className="flex gap-2">
                        <Input
                          value={promoInput}
                          onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyPromo(); } }}
                          placeholder="ex. LUNE10"
                          className="bg-[#1B132B] border-white/10 uppercase text-sm"
                          maxLength={32}
                          data-testid="promo-input"
                        />
                        <Button
                          type="button"
                          onClick={handleApplyPromo}
                          disabled={promoLoading || !promoInput.trim()}
                          variant="outline"
                          className="border-[#D926A9]/40 text-[#D926A9] hover:bg-[#D926A9]/10"
                          data-testid="promo-apply-btn"
                        >
                          {promoLoading ? '...' : 'Appliquer'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/30" data-testid="promo-applied">
                      <div className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-400" />
                        <span className="font-mono font-medium text-green-400">{promoApplied.code}</span>
                        <span className="text-[#B59ED1]">
                          {promoApplied.discount_type === 'percentage' ? `-${promoApplied.value}%` : `-${promoApplied.value}€`}
                        </span>
                      </div>
                      <button onClick={handleRemovePromo} className="text-[#B59ED1] hover:text-white" data-testid="promo-remove-btn" title="Retirer">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#B59ED1]">Sous-total</span>
                    <span>{cartTotal.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#B59ED1]">Livraison</span>
                    <span className={shippingCost === 0 ? "text-green-400" : ""}>
                      {isGiftCardOnly ? "Digitale (0 €)" : (shippingCost === 0 ? "Gratuit" : `${shippingCost.toFixed(2)} €`)}
                    </span>
                  </div>
                  {promoApplied && (
                    <div className="flex justify-between text-sm" data-testid="promo-discount-line">
                      <span className="text-green-400">Remise ({promoApplied.code})</span>
                      <span className="text-green-400">-{discountAmount.toFixed(2)} €</span>
                    </div>
                  )}
                  <Separator className="bg-white/10" />
                  <div className="flex justify-between">
                    <span className="text-lg font-medium">Total</span>
                    <span className="text-2xl font-serif text-[#D926A9]">{finalTotal.toFixed(2)} €</span>
                  </div>
                </div>

                {/* Choix paiement pour Click & Collect */}
                {!isGiftCardOnly && deliveryMethod === "click_collect" && (
                  <div className="mt-5 space-y-2" data-testid="click-collect-payment-choice">
                    <label className="text-xs uppercase tracking-wider text-[#B59ED1]">Mode de paiement</label>
                    <RadioGroup value={clickCollectPayment} onValueChange={setClickCollectPayment} className="space-y-2">
                      <div className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${clickCollectPayment === 'online' ? 'border-[#00E5FF] bg-[#00E5FF]/10' : 'border-white/10 hover:border-white/30'}`}>
                        <RadioGroupItem value="online" id="cc-online" className="mt-0.5" />
                        <Label htmlFor="cc-online" className="flex-1 cursor-pointer text-sm">
                          <span className="font-medium text-[#F7F3FC]">Payer maintenant en ligne</span>
                          <p className="text-[#B59ED1] text-xs mt-0.5">Carte bancaire sécurisée (Square)</p>
                        </Label>
                      </div>
                      <div className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${clickCollectPayment === 'on_pickup' ? 'border-[#D926A9] bg-[#D926A9]/10' : 'border-white/10 hover:border-white/30'}`}>
                        <RadioGroupItem value="on_pickup" id="cc-onpickup" className="mt-0.5" />
                        <Label htmlFor="cc-onpickup" className="flex-1 cursor-pointer text-sm">
                          <span className="font-medium text-[#F7F3FC]">Payer en boutique au retrait</span>
                          <p className="text-[#B59ED1] text-xs mt-0.5">Espèces ou CB sur place</p>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                )}

                <label className="flex items-start gap-2 text-xs text-[#B59ED1] mt-5 mb-3 cursor-pointer leading-relaxed">
                  <input
                    type="checkbox"
                    checked={rgpdConsent}
                    onChange={(e) => setRgpdConsent(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded accent-[#D926A9] flex-shrink-0"
                    data-testid="checkout-rgpd-checkbox"
                  />
                  <span>
                    J'accepte la <Link to="/politique-de-confidentialite" target="_blank" className="text-[#00E5FF] hover:underline">politique de confidentialité</Link> et les <Link to="/cgv" target="_blank" className="text-[#00E5FF] hover:underline">CGV</Link>.
                  </span>
                </label>

                <Button
                  onClick={handleCreateOrder}
                  disabled={loading || !rgpdConsent || (!isGiftCardOnly && deliveryMethod === "click_collect" && !selectedSlot)}
                  className="w-full mt-2 gradient-primary text-lg py-6"
                  data-testid="checkout-submit-btn"
                >
                  {loading ? "Traitement..."
                    : isGiftCardOnly
                      ? "Payer ma carte cadeau"
                      : deliveryMethod === "click_collect" && clickCollectPayment === "on_pickup"
                        ? "Confirmer (paiement en boutique)"
                        : "Passer au paiement"}
                </Button>

                {!isGiftCardOnly && deliveryMethod === "click_collect" && clickCollectPayment === "on_pickup" && (
                  <p className="text-center text-xs text-[#B59ED1] mt-4">
                    Paiement à effectuer en boutique lors du retrait
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Modale de connexion inline (pour ne pas perdre le contexte du checkout) */}
      <CheckoutLoginModal
        open={showLoginModal}
        onOpenChange={(v) => {
          setShowLoginModal(v);
          // Si l'utilisateur ferme la modale sans se connecter, on le ramène au panier
          if (!v && !user) navigate('/panier');
        }}
        onLoggedIn={(u) => {
          setUser(u);
          setShowLoginModal(false);
          toast.success("Connexion réussie. Tu peux finaliser ta commande.");
        }}
      />
    </main>
  );
}

// =============================================================
// Modale de connexion in-page (au lieu de naviguer vers /connexion)
// Permet à l'utilisateur de rester sur le checkout, sans perdre
// son panier ni scroller en haut de page.
// =============================================================
function CheckoutLoginModal({ open, onOpenChange, onLoggedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password, remember: true }, { withCredentials: true });
      onLoggedIn(data.user);
    } catch (err) {
      toast.error(extractApiError(err, "Email ou mot de passe incorrect."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#261B3B] border-white/10 max-w-md" data-testid="checkout-login-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white">Connexion</DialogTitle>
          <DialogDescription className="text-sm text-[#B59ED1]">
            Connecte-toi pour finaliser ta commande. Tu retrouveras ton panier juste après.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="checkout-login-form">
          <div className="space-y-1.5">
            <Label htmlFor="ck-email" className="text-[#E2D5F8] text-sm">Email</Label>
            <Input
              id="ck-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ton@email.com"
              className="bg-[#1B132B] border-white/10 h-11 text-white"
              data-testid="checkout-login-email"
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="ck-password" className="text-[#E2D5F8] text-sm">Mot de passe</Label>
              <Link
                to="/mot-de-passe-oublie"
                className="text-xs text-[#00E5FF] hover:underline"
                data-testid="checkout-login-forgot"
                onClick={() => onOpenChange(false)}
              >
                Oublié ?
              </Link>
            </div>
            <div className="relative">
              <Input
                id="ck-password"
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-[#1B132B] border-white/10 h-11 text-white pr-10"
                data-testid="checkout-login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B59ED1] hover:text-white text-xs"
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? 'Masquer' : 'Voir'}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-gradient-to-r from-[#D926A9] to-[#00E5FF] hover:opacity-90 text-white"
            data-testid="checkout-login-submit"
          >
            {loading ? "Connexion…" : "Se connecter et finaliser ma commande"}
          </Button>
        </form>

        <div className="pt-3 mt-3 border-t border-white/10">
          <p className="text-xs text-center text-[#B59ED1]">
            Pas encore de compte ?{' '}
            <Link
              to="/inscription?next=/checkout"
              className="text-[#00E5FF] hover:underline font-medium"
              data-testid="checkout-login-to-register"
              onClick={() => {
                try { sessionStorage.setItem('post_login_redirect', '/checkout'); } catch (_e) { /* sessionStorage indisponible */ }
              }}
            >
              Crée ton compte ici
            </Link>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
